<template>
  <div
    ref="preloaderEl"
    class="[ preloader ] absolute inset-0 flex items-center justify-center p-10 m-6 md:m-12 lg:m-24 rounded-md text-blue-200 uppercase font-bold"
  >
    <span class="dot">.</span><span class="dot">.</span><span class="dot">.</span>Loading<span class="dot">.</span><span class="dot">.</span><span class="dot">.</span>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import gsap from 'gsap';
import { fadeInPreloader, fadeOutPreloader } from '@/animations/preloader';

defineOptions({ name: 'Preloader' });

const props = defineProps({
  ready: { type: Boolean, default: false }
});

const emit = defineEmits(['hide-start', 'hidden']);

const preloaderEl = ref(null);
const shown = ref(false);
const hiding = ref(false);
const hidden = ref(false);

const maybeHide = () => {
  if (!props.ready || !shown.value || hiding.value || hidden.value) return;

  hiding.value = true;
  fadeOutPreloader(preloaderEl.value, {
    duration: 0.5,
    onStart: () => emit('hide-start'),
    onComplete: () => {
      hidden.value = true;
      emit('hidden');
    }
  });
};

onMounted(() => {
  fadeInPreloader(preloaderEl.value, {
    duration: 1,
    onComplete: () => {
      shown.value = true;
      maybeHide();
    }
  });
});

onBeforeUnmount(() => {
  if (preloaderEl.value) gsap.killTweensOf(preloaderEl.value);
});

watch(() => props.ready, maybeHide);
</script>

<style scoped>
.preloader {
  opacity: 0;
  visibility: hidden;
  animation: letter-spacing 1s ease infinite alternate;
  background: linear-gradient(135deg, rgba(140, 171, 190, 0.3) -4%, rgba(65, 90, 139, 0.3) 100%);
}

.dot { animation: dots 1s linear infinite alternate; }
.dot:nth-child(1) { animation-delay: 0s; }
.dot:nth-child(2) { animation-delay: 0.2s; }
.dot:nth-child(3) { animation-delay: 0.4s; }
.dot:nth-child(4) { animation-delay: 0.6s; }
.dot:nth-child(5) { animation-delay: 0.8s; }
.dot:nth-child(6) { animation-delay: 1s; }

@keyframes letter-spacing {
  0% { letter-spacing: 0.05em; }
  92%, 100% { letter-spacing: 0.15em; }
}

@keyframes dots {
  0%, 50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
